import axios from 'axios'
import axiosInstance from '../helpers/axiosInstance'
import { validateTransactionStatus, TRANSACTION_STATUS_SUCCESS } from '../services/concurrencyService'

export const fetchIntegrationShippingMethods = async id => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/integration_shipping_methods/channel_integration/${id}`)
  return res.data
}

export const fetchCubboShippingMethods = async () => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_method`)
  return res.data
}
export const fetchWarehouseCubboShippingMethods = async store_id => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/warehouses/${store_id}/shipping-methods`)
  return res.data
}

export const fetchOrderShippingPackages = async (order_id, includeGlobalPackages = true) => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/fulfillment-tasks/shipping-packages/${order_id}`, {
    params: { global_packages: includeGlobalPackages },
  })
  return res.data
}

export const syncIntegrationsShippingMethods = async id => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/channel_integrations/synchronize_shipping_methods/${id}`)
  return res.data
}

export const getShipingLabels = async params => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_label`, { params: params })
  return res.data
}

export const getPackingPrintBundle = async (orderId, sources) => {
  const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/orders/wms/${orderId}/packing-print-bundle`, { sources: sources })
  return res.data
}

export const getLabel = async id => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_label/${id}`)
  return res.data
}

/**
 *
 */
export const saveMatchShippingMethods = async shipping_matches => {
  const data = {
    data: shipping_matches,
  }
  const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_matches`, data)
  return res.data
}

const getShippingInfo = (shippingAddress, shippingDetails, productsData, trackingNumber) => {
  const shippingPackages = shippingDetails.packages.map(shippingPackage => ({
    id: shippingPackage.id,
    quantity: 1,
    length_cm: shippingPackage.length_cm,
    width_cm: shippingPackage.width_cm,
    height_cm: shippingPackage.height_cm,
    weight_kg: shippingPackage.weight_kg,
    volumetric_weight: shippingPackage.volumetric_weight,
  }))

  const data = {
    destination: {
      first_name: shippingAddress.firstName,
      last_name: shippingAddress.lastName,
      phone: shippingAddress.phone,
      email: shippingAddress.email,
      address1: shippingAddress.address,
      address2: shippingAddress.address2,
      country: shippingAddress.country.value,
      city: shippingAddress.city,
      province: shippingAddress.province,
      zip_code: shippingAddress.zipCode,
      number: shippingAddress.number,
      references: shippingAddress.references,
    },
    shipping_details: {
      shipping_method: shippingDetails.shippingMethod.id,
      shipping_date: shippingDetails.shippingDate,
      description: shippingDetails.description,
      package_id: shippingDetails.package_id,
      packages: shippingPackages,
      packages_count: shippingDetails.packages_count,
    },
    tracking_number: trackingNumber,
    ...(productsData && { shipment: { products_data: productsData } }),
  }
  return data
}

export const generateLabel = async (shipment, shippingAddress, shippingDetails, trackingNumber, cameraStatus = null) => {
  const data = {
    ...getShippingInfo(shippingAddress, shippingDetails, shipment.productsData, trackingNumber),
    ...(cameraStatus ? { camera_status: cameraStatus } : {}),
  }
  const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_label/generate/${shipment.orderId}`, data)

  // 202 means the carrier accepted the shipment and the label exists, but its PDF is still
  // downloading (FM Transportes: measured p50 17s, 94.9% under a minute). It is NOT a label.
  // Returning res.data blind here handed the caller `{status:'processing'}` as if it were one.
  if (res.status === 202 && res.data?.transaction_key) {
    return {
      pending: true,
      transactionKey: res.data.transaction_key,
      shippingLabelId: res.data.shipping_label_id,
      estimatedSeconds: res.data.estimated_seconds,
      // Why the server is making the operator wait, when it is not the default PDF download.
      waitReason: res.data.wait_reason,
    }
  }

  return res.data
}

// Polls the job the 202 above opened. Resolves to one of:
//   { ready: true, label }       — the PDF landed; same shape as the synchronous 201
//   { pending: true }            — still downloading, keep polling
//   { failed: true, error }      — the carrier never produced it; `error` carries the
//                                  fulfillment_error_* fields the error card reads
//   { unknown: true }            — nothing to poll (expired and no label): stop polling
export const fetchGenerateLabelStatus = async (orderId, transactionKey) => {
  try {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_label/generate/${orderId}/status`, {
      params: { transaction_key: transactionKey },
    })

    // The done response is the serialized label with no `status` key, deliberately
    // identical to the 201 body so both paths print through the same code.
    if (!res.data?.status) return { ready: true, label: res.data }
    if (res.data.status === 'error') return { failed: true, error: res.data }
    return { pending: true }
  } catch (error) {
    // 404 = the Redis entry expired AND no label has a PDF. Nothing left to wait for.
    if (error?.response?.status === 404) return { unknown: true }
    throw error
  }
}

// What the card asks when its countdown reaches zero: what the server cycle did
// while the operator waited. Answers "ready" when the prerequisite has
// arrived and it can retry, "waiting" when the cycle opened another round (with its new time and
// attempt number), and "exhausted" when they ran out and the case went to Tech.
export const fetchPrerequisiteStatus = async orderId => {
  try {
    const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_label/generate/${orderId}/prerequisite`)
    return res.data
  } catch (error) {
    // 404 = the order no longer exists or is not visible. There is nothing to wait for.
    if (error?.response?.status === 404) return { status: 'unknown' }
    throw error
  }
}

export const generateGroupOrderLabel = async (referenceOrderId, storeId, groupingOrders, shippingDetails, shippingAddress) => {
  const data = {
    shipping_details: {
      shipping_method: shippingDetails.shippingMethod ? shippingDetails.shippingMethod.id : null,
      shipping_date: shippingDetails.shippingDate,
      description: shippingDetails.description,
      package_id: shippingDetails.package_id,
      package: {
        id: shippingDetails.packages[0].id,
        quantity: 1,
      },
    },
    destination: {
      first_name: shippingAddress.firstName,
      last_name: shippingAddress.lastName,
      phone: shippingAddress.phone,
      email: shippingAddress.email,
      address1: shippingAddress.address,
      address2: shippingAddress.address2,
      country: shippingAddress.country.value,
      city: shippingAddress.city,
      province: shippingAddress.province,
      zip_code: shippingAddress.zipCode,
    },
    grouping_orders: groupingOrders,
    reference_order_id: referenceOrderId,
    store_id: storeId,
  }
  try {
    const res = await axiosInstance.post(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-label/generate-shared-label`, data)
    if (res.status === 202 && res.data.transaction_id) {
      const transactionStatus = await validateTransactionStatus(res.data.transaction_id)
      console.log('transactionStatus: ', transactionStatus)
      if (transactionStatus != TRANSACTION_STATUS_SUCCESS) {
        throw new Error('Error')
      }
    }
    return res.data
  } catch (error) {
    throw error
  }
}

export const fetchShippingMethods = async (params = {}) => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_methods`, { params })
  return res.data
}

export const fetchShippingMethodCoverage = async shipping_method_id => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_methods/${shipping_method_id}/coverage`)
  return res.data
}

export const updateShippingMethodCoverage = async (shipping_method_id, coverage_data) => {
  const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_methods/${shipping_method_id}/coverage`, coverage_data)
  return res.data
}

export const uploadShippingMethodCoverageByRange = async (shipping_method_id, csvFile) => {
  const formData = new FormData()
  formData.append('file', csvFile)
  const res = await axiosInstance.post(
    `${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping_methods/${shipping_method_id}/coverage/upload-by-range`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    },
  )
  return res.data
}

// ---------------------------------------------------------------------------
// Coverage update payloads
// ---------------------------------------------------------------------------

// The columns ShippingCoverages::UpdateAttributes::ATTRIBUTE_NAMES accepts on the four
// update endpoints. Building the payload in ONE place is the point: the three callers
// below used to each copy the same enabled / SLA / offset triple by hand, so when the
// API opened `zone` and `extended_zone` the WMS kept dropping them — silently, because
// an ignored attribute still answers 202 and the job still reports success.
//
// `false` is a MEANINGFUL value for both booleans ("disable" and "remove extended
// zone"), so every check probes for undefined/null/'' and never for truthiness —
// the same trap the backend documents in UpdateAttributes.read.
const isBlank = v => v === undefined || v === null || v === ''
const toBool = v => v === true || v === 'true'

/**
 * source: any object carrying the operator's choices (a form, or one CSV row already
 * mapped). Keys absent from `source` stay absent from the payload, which is what leaves
 * the corresponding column untouched.
 *
 * Nothing is filtered per endpoint. update-by-shipping-method refuses a method-wide
 * `zone` with ZONE_UPDATE_NOT_ALLOWED_METHOD_WIDE — that refusal is the backend's to
 * make and it is now translated, whereas dropping the attribute here would recreate
 * exactly the silent 202-that-applied-nothing this module exists to end.
 */
export const buildCoverageUpdateAttributes = (source = {}) => {
  const data = {}

  if (!isBlank(source.enabled)) data.enabled = toBool(source.enabled)

  if (!isBlank(source.delivery_sla_in_hours)) {
    data.delivery_sla_in_hours = parseInt(source.delivery_sla_in_hours, 10)
  }
  if (!isBlank(source.delivery_sla_offset)) {
    data.delivery_sla_offset = parseInt(source.delivery_sla_offset, 10)
  }

  // Reclassification. Only undefined/null/'' mean "leave it alone"; anything else is sent,
  // coerced and trimmed. Testing `typeof === 'string'` instead would silently swallow a
  // zone that arrived as a number (zones like `3` really exist) — the same silent drop
  // this module was written to remove.
  if (!isBlank(source.zone)) {
    const zone = String(source.zone).trim()
    // A whitespace-only cell is the operator leaving the column blank, not a zone name.
    if (zone !== '') data.zone = zone
  }

  // The ODA/OPA surcharge flag — a different column from `zone`, even though ops calls
  // both "extendida". Ships false so the surcharge can be REMOVED, not only added.
  if (!isBlank(source.extended_zone)) data.extended_zone = toBool(source.extended_zone)

  return data
}

export const updateShippingCoverageByRange = async (shipping_method_id, ranges) => {
  const data = {
    shipping_method_id: parseInt(shipping_method_id, 10),
    // Each range carries its own attributes, so a CSV can reclassify several zones in
    // one request. The caller's `range_ends` / `delivery_sla` spellings are this
    // endpoint's only aliases; everything else goes through the shared builder.
    ranges: ranges.map(range => ({
      range_start: range.range_start,
      range_end: range.range_ends,
      ...buildCoverageUpdateAttributes({
        ...range,
        // `delivery_sla` is this endpoint's alias for delivery_sla_in_hours. Switch on
        // whether the caller SENT the alias, not on whether it holds a value: with `??`,
        // a caller passing `delivery_sla: ''` (which every CSV row does) alongside a real
        // delivery_sla_in_hours would have the empty alias win and the SLA vanish.
        ...('delivery_sla' in range ? { delivery_sla_in_hours: range.delivery_sla } : {}),
      }),
    })),
  }
  const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-coverages/update-by-range`, data)
  return res.data
}

export const fetchShippingCoverageUpdateStatus = async jid => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-coverages/update-status/${jid}`)
  return res.data
}

export const fetchShippingCoveragesList = async params => {
  const res = await axiosInstance.get(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-coverages`, { params })
  return res.data
}

export const bulkUpdateShippingCoveragesByIds = async (shipping_method_id, coverageIds, updateData) => {
  const data = {
    shipping_method_id: parseInt(shipping_method_id, 10),
    coverage_ids: coverageIds.map(id => parseInt(id, 10)),
    ...buildCoverageUpdateAttributes(updateData),
  }

  const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-coverages/bulk-update-by-ids`, data)
  return res.data
}

export const updateAllShippingCoveragesByMethod = async (shipping_method_id, coverageData) => {
  const data = {
    shipping_method_id: parseInt(shipping_method_id, 10),
    // No zone filter: the form offers no zone control here, and if a caller ever sends
    // one the backend answers ZONE_UPDATE_NOT_ALLOWED_METHOD_WIDE, which the modal
    // translates into "hazlo por CP, por rango o sobre una selección" — a refusal the
    // operator can act on, instead of a request that reports success and changes nothing.
    ...buildCoverageUpdateAttributes(coverageData),
  }

  const res = await axiosInstance.patch(`${process.env.REACT_APP_PLATFORM_URL}/api/v1/shipping-coverages/update-by-shipping-method`, data)
  return res.data
}
